import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, AppWindow, AudioLines, ChevronRight, DownloadCloud, Download, ExternalLink, File as FileIcon, Film, Folder, FolderOpen, HardDrive, Loader2, RefreshCw, X,
} from 'lucide-react';
import {
  LOCAL_FOLDER_ACCESS_EVENT, getLocalFolderForBrowsing, openLocalAccountFolder, openLocalWorkspaceFolder, requestLocalFolderAccess, type LocalFolderBrowse,
} from '../utils/localSave';
import {
  FOLDER_HELPER_SETUP_NAME, canUseFolderHelper, downloadFolderHelperSetup, folderHelperUrl, isFolderHelperReady, openWithFolderHelper, setFolderHelperReady,
} from '../utils/folderHelper';
import { catchUpSummary, type CatchUpProgress, type CatchUpResult } from '../utils/folderCatchUp';
import { captureVideoFrame } from '../utils/videoThumbnail';
import { VideoPlayer } from './VideoPlayer';
import { useMediaThumbnails, type ThumbnailRequest } from './nodes/quickEditor/useMediaThumbnails';

interface LocalFolderWindowProps {
  userId?: string;
  /** The open workspace: the window starts in its folder when there is one. */
  workspaceName?: string;
  onClose: () => void;
  /** Pick a folder (or pick it again), as Settings does. */
  onChooseFolder: () => void;
  /** Every file of the library that is not in the folder yet, copied into it (utils/folderCatchUp.ts); absent when signed out. */
  onCopyCloudFiles?: (onProgress: (progress: CatchUpProgress) => void, signal: AbortSignal) => Promise<CatchUpResult>;
}

type Kind = 'image' | 'video' | 'audio' | 'other';

interface Entry {
  name: string;
  folder?: FileSystemDirectoryHandle;
  file?: File;
  kind?: Kind;
}

const kindOf = (file: File): Kind => {
  const type = file.type || '';
  if (type.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(file.name)) return 'image';
  if (type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(file.name)) return 'video';
  if (type.startsWith('audio/') || /\.(mp3|wav|m4a|ogg)$/i.test(file.name)) return 'audio';
  return 'other';
};

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** The second action in a bar: outlined. */
const OUTLINED_BUTTON = 'ns-btn ns-btn-ghost ns-btn-sm ns-btn-flat';
/** An action on the dark preview. */
const ON_DARK_BUTTON = 'ns-btn ns-btn-ghost-dark ns-btn-xs ns-btn-flat';

const KIND_ICON: Record<Kind, React.ReactNode> = {
  image: <FileIcon size={24} />,
  video: <Film size={24} />,
  audio: <AudioLines size={24} />,
  other: <FileIcon size={24} />,
};

/**
 * A window onto the user's local auto-save folder: browse the workspace folders and
 * their Videos, Audio and image folders, see thumbnails, and preview a file. On Windows,
 * "Open in File Explorer" opens the same folder through a helper set up once; elsewhere
 * "Show in system window" opens the browser's own file window at the same place.
 */
export const LocalFolderWindow: React.FC<LocalFolderWindowProps> = ({ userId, workspaceName, onClose, onChooseFolder, onCopyCloudFiles }) => {
  const [access, setAccess] = useState<LocalFolderBrowse | null>(null);
  const [path, setPath] = useState<FileSystemDirectoryHandle[]>([]);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ file: File; url: string; kind: Kind } | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  // File Explorer: the setup steps (and the file to select once set up), and a note after opening
  const [helperSetup, setHelperSetup] = useState<{ file?: string } | null>(null);
  const [explorerNote, setExplorerNote] = useState(0);
  // Every file of the library copied into the folder: where the work is, and what came of it
  const [copying, setCopying] = useState<{ progress: CatchUpProgress | null; result: CatchUpResult | null; error: string | null } | null>(null);
  const copyController = useRef<AbortController | null>(null);
  useEffect(() => () => copyController.current?.abort(), []);
  const copyingNow = !!copying && !copying.result && !copying.error;
  const copyCloudFiles = async () => {
    if (!onCopyCloudFiles || copyController.current) return;
    const controller = new AbortController();
    copyController.current = controller;
    setCopying({ progress: null, result: null, error: null });
    try {
      const result = await onCopyCloudFiles((progress) => { if (!controller.signal.aborted) setCopying({ progress, result: null, error: null }); }, controller.signal);
      setCopying({ progress: null, result, error: null });
      if (result.copied > 0) setRefreshToken(n => n + 1);
    } catch (e: any) {
      setCopying({ progress: null, result: null, error: e?.message || 'The files could not be copied.' });
    } finally {
      copyController.current = null;
    }
  };
  const urlsRef = useRef<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = path[path.length - 1];

  // Where the folder stands, and again when access is granted or the folder changes
  useEffect(() => {
    let alive = true;
    const check = () => {
      getLocalFolderForBrowsing(userId).then(next => { if (alive) setAccess(next); }, () => { if (alive) setAccess({ state: 'none' }); });
    };
    check();
    window.addEventListener(LOCAL_FOLDER_ACCESS_EVENT, check);
    return () => { alive = false; window.removeEventListener(LOCAL_FOLDER_ACCESS_EVENT, check); };
  }, [userId]);

  // Start in the open workspace's folder when it has one, else in the account's own folder
  useEffect(() => {
    if (access?.state !== 'granted') return;
    let alive = true;
    const root = access.root;
    void (async () => {
      // The workspace's folder, with the account's folder, the project and the folders on the way to it
      const start = (workspaceName ? await openLocalWorkspaceFolder(root, workspaceName, userId) : null)
        ?? (await openLocalAccountFolder(root, userId))
        ?? [root];
      if (alive) setPath(start);
    })();
    return () => { alive = false; };
  }, [access, workspaceName, userId]);

  const releaseUrls = () => {
    for (const url of urlsRef.current) URL.revokeObjectURL(url);
    urlsRef.current = [];
  };
  useEffect(() => () => releaseUrls(), []);

  // The current folder's contents: folders first, then files, newest first
  useEffect(() => {
    if (!current) return;
    let alive = true;
    setEntries(null);
    setError(null);
    void (async () => {
      try {
        const folders: Entry[] = [];
        const files: Entry[] = [];
        for await (const [name, handle] of (current as any).entries() as AsyncIterable<[string, FileSystemHandle]>) {
          if (handle.kind === 'directory') folders.push({ name, folder: handle as FileSystemDirectoryHandle });
          else {
            const file = await (handle as FileSystemFileHandle).getFile();
            files.push({ name, file, kind: kindOf(file) });
          }
        }
        folders.sort((a, b) => a.name.localeCompare(b.name));
        files.sort((a, b) => (b.file!.lastModified - a.file!.lastModified));
        if (alive) setEntries([...folders, ...files]);
      } catch (e: any) {
        if (alive) setError(e?.message || 'This folder could not be read.');
      }
    })();
    return () => { alive = false; };
  }, [current, refreshToken]);

  // Pictures for images (the file itself) and videos (a frame, kept in this browser)
  const imageUrls = useMemo(() => {
    releaseUrls();
    const urls: Record<string, string> = {};
    for (const entry of entries || []) {
      if (entry.kind === 'image' && entry.file) {
        const url = URL.createObjectURL(entry.file);
        urlsRef.current.push(url);
        urls[entry.name] = url;
      }
    }
    return urls;
  }, [entries]);
  const pathKey = path.map(dir => dir.name).join('/');
  const videoKey = (entry: Entry) => `local:${pathKey}/${entry.name}:${entry.file!.lastModified}:${entry.file!.size}`;
  const thumbnailRequests: ThumbnailRequest[] = (entries || []).filter(entry => entry.kind === 'video' && entry.file).map(entry => ({
    key: videoKey(entry),
    keep: true,
    make: async () => {
      const url = URL.createObjectURL(entry.file!);
      try {
        return await captureVideoFrame(url);
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  }));
  const thumbnails = useMediaThumbnails(thumbnailRequests);

  const openPreview = useCallback((file: File) => {
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file), kind: kindOf(file) };
    });
  }, []);
  const closePreview = useCallback(() => {
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  /** The system's own file window, opened at the current folder; a file chosen there is previewed here. */
  const showInSystemWindow = async () => {
    try {
      const [handle] = await (window as any).showOpenFilePicker({ startIn: current, multiple: false });
      if (handle) openPreview(await handle.getFile());
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(e?.message || 'The system window could not be opened.');
    }
  };

  const useExplorer = canUseFolderHelper();
  const rootName = path[0]?.name || '';
  const launchExplorer = (file?: string) => {
    if (path.length === 0) return;
    openWithFolderHelper(folderHelperUrl(rootName, path.slice(1).map(dir => dir.name), file));
    setExplorerNote(n => n + 1);
  };
  /** File Explorer at the current folder, with the file selected when there is one; the helper is set up first. */
  const openInExplorer = (file?: string) => {
    if (isFolderHelperReady()) launchExplorer(file);
    else setHelperSetup({ file });
  };
  const finishHelperSetup = () => {
    setFolderHelperReady(true);
    const file = helperSetup?.file;
    setHelperSetup(null);
    launchExplorer(file);
  };
  useEffect(() => {
    if (!explorerNote) return;
    const timer = setTimeout(() => setExplorerNote(0), 8000);
    return () => clearTimeout(timer);
  }, [explorerNote]);

  useEffect(() => { rootRef.current?.focus(); }, []);
  // Keys are caught for the whole page while the window is open: focus may be anywhere after a click.
  // Esc steps back (preview, then window); the canvas's select-all, copy and paste stay out of it.
  const escapeRef = useRef(() => {});
  escapeRef.current = () => (helperSetup ? setHelperSetup(null) : preview ? closePreview() : onClose());
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const shortcut = (e.ctrlKey || e.metaKey) && ['a', 'c', 'v'].includes(e.key.toLowerCase());
      if (e.key !== 'Escape' && !shortcut) return;
      e.stopPropagation();
      if (e.key === 'Escape') escapeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const body = (() => {
    if (!access) return <Centered><Loader2 size={20} className="animate-spin text-ink-faint" /></Centered>;
    if (access.state === 'unsupported') {
      return <Message tone="warn" icon={<AlertTriangle />} title="This browser cannot open local folders" text="Use Chrome or Edge on a computer to see your local folder here." />;
    }
    if (access.state === 'none') {
      return (
        <Message icon={<HardDrive />} title="No local folder yet" text="Choose a folder on your computer. Generated images and videos are saved there, and the app reads them from there instead of downloading them.">
          <PrimaryButton onClick={onChooseFolder}><FolderOpen size={14} /> Choose a folder</PrimaryButton>
        </Message>
      );
    }
    if (access.state === 'prompt') {
      return (
        <Message icon={<HardDrive />} title={`Allow access to ${access.root.name || 'your folder'}`} text="After a restart the browser asks again before this app may open your folder.">
          <PrimaryButton onClick={() => void requestLocalFolderAccess(userId)}><HardDrive size={14} /> Allow access</PrimaryButton>
        </Message>
      );
    }
    if (access.state === 'denied') {
      return (
        <Message tone="warn" icon={<AlertTriangle />} title="Access to the folder was refused" text="Choose the folder again to give this app access.">
          <PrimaryButton onClick={onChooseFolder}><FolderOpen size={14} /> Choose the folder again</PrimaryButton>
        </Message>
      );
    }
    if (error) return <Message tone="danger" icon={<AlertTriangle />} title="This folder could not be read" text={error} />;
    if (!entries) return <Centered><Loader2 size={20} className="animate-spin text-ink-faint" /></Centered>;
    if (entries.length === 0) return <Message icon={<Folder />} title="This folder is empty" text="Files the app saves for this workspace show up here." />;
    return (
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 px-6 py-5" data-local-folder-list>
        {entries.map(entry => {
          const thumb = entry.kind === 'image' ? imageUrls[entry.name] : entry.kind === 'video' ? thumbnails[videoKey(entry)] : undefined;
          return (
            <li key={entry.name}>
              <button
                type="button"
                onClick={() => (entry.folder ? setPath(prev => [...prev, entry.folder!]) : openPreview(entry.file!))}
                className="w-full text-left rounded-xl bg-white border border-cream-300 hover:border-lavender hover:shadow-hair transition-colors overflow-hidden"
                title={entry.name}
                data-entry={entry.folder ? 'folder' : entry.kind}
              >
                <span className="grid aspect-video place-items-center bg-cream text-ink-faint overflow-hidden">
                  {entry.folder ? <Folder size={32} className="text-lavender-deep" />
                    : thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" draggable={false} />
                    : KIND_ICON[entry.kind || 'other']}
                </span>
                <span className="block px-2.5 py-2">
                  <span className="block truncate text-xs font-semibold text-ink">{entry.name}</span>
                  <span className="ns-t-meta block truncate">
                    {entry.folder ? 'Folder' : `${formatSize(entry.file!.size)} · ${formatDate(entry.file!.lastModified)}`}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  })();

  return (
    <div className="ns-modal-scrim z-[150]" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={rootRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Local folder"
        // Focused only so keys reach it; a ring around the whole window would just be noise
        style={{ outline: 'none' }}
        className="ns-modal-sheet ns-modal-sheet-flush max-w-[1000px] h-[min(680px,calc(100vh-32px))] outline-none"
        data-local-folder-window
      >
        {/* The head of every dialog: its title, and the round button that closes it at the right */}
        <div className="flex items-center gap-2 pl-6 pr-5 pt-5 pb-3 shrink-0">
          <h2 className="ns-t-dialog flex-1 min-w-0 truncate">Local folder</h2>
          {current && (
            <>
              <IconButton label="Refresh" onClick={() => setRefreshToken(n => n + 1)}><RefreshCw /></IconButton>
              {onCopyCloudFiles && (
                <button
                  type="button"
                  onClick={() => void copyCloudFiles()}
                  disabled={copyingNow}
                  className={`${OUTLINED_BUTTON} shrink-0`}
                  title="Copy every file of your library that is not in this folder yet into it, each downloaded once"
                  data-copy-cloud-files
                >
                  <DownloadCloud size={14} /> Copy cloud files here
                </button>
              )}
              {useExplorer ? (
                <button
                  type="button"
                  onClick={() => openInExplorer()}
                  className={`${OUTLINED_BUTTON} shrink-0`}
                  title="Open this folder in File Explorer"
                  data-open-explorer
                >
                  <FolderOpen size={14} /> Open in File Explorer
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void showInSystemWindow()}
                  className={`${OUTLINED_BUTTON} shrink-0`}
                  title="Open this folder in your computer's own file window"
                >
                  <AppWindow size={14} /> Show in system window
                </button>
              )}
            </>
          )}
          <IconButton label="Close" onClick={onClose}><X /></IconButton>
        </div>
        {/* Where you are: the folder's name, then each folder opened inside it */}
        {path.length > 0 && (
          <nav className="flex items-center gap-0.5 min-w-0 pl-3 pr-5 pb-3 shrink-0" aria-label="Folder path">
            {path.map((dir, index) => (
              <React.Fragment key={`${index}-${dir.name}`}>
                {index > 0 && <ChevronRight size={14} className="text-ink-faint shrink-0" aria-hidden="true" />}
                {index === path.length - 1 ? (
                  <span className="min-w-0 truncate px-3 text-xs font-semibold text-ink" aria-current="page">{dir.name || 'Local folder'}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPath(prev => prev.slice(0, index + 1))}
                    className="ns-btn ns-btn-quiet ns-btn-xs ns-btn-flat min-w-0"
                    title={dir.name || 'Local folder'}
                  >
                    <span className="truncate">{dir.name || 'Local folder'}</span>
                  </button>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        {copying && (
          <div className="shrink-0 flex items-center gap-2 px-6 pb-3 text-xs text-ink-soft" role="status" data-copy-cloud-status>
            {copyingNow ? <Loader2 size={14} className="animate-spin shrink-0" aria-hidden="true" /> : <DownloadCloud size={14} className="text-lavender-deep shrink-0" aria-hidden="true" />}
            <span className="min-w-0 truncate flex-1">
              {copying.error ? copying.error
                : copying.result ? (catchUpSummary(copying.result, 'this folder') ?? (copying.result.stopped ? 'Nothing was copied: this folder cannot be written right now.' : `Everything is here already (${copying.result.present} file${copying.result.present === 1 ? '' : 's'}).`))
                : copying.progress ? `Copying ${copying.progress.done + 1} of ${copying.progress.total}: ${copying.progress.filename}` : 'Reading your library'}
            </span>
            {copyingNow ? (
              <button type="button" onClick={() => copyController.current?.abort()} className="ns-btn ns-btn-quiet ns-btn-xs ns-btn-flat">Stop</button>
            ) : (
              <button type="button" onClick={() => setCopying(null)} aria-label="Dismiss" className="ns-icon-btn ns-icon-btn-xs"><X /></button>
            )}
          </div>
        )}

        <div className="relative flex-1 min-h-0 overflow-y-auto border-t border-cream-300">
          {body}
          {preview && (
            <div className="absolute inset-0 flex flex-col bg-ink text-cream" data-local-preview>
              <div className="flex items-center gap-2 px-4 h-12 shrink-0 border-b border-white/10">
                <span className="truncate text-sm font-semibold flex-1" title={preview.file.name}>{preview.file.name}</span>
                <span className="text-xs text-cream/70 shrink-0">{formatSize(preview.file.size)}</span>
                <a href={preview.url} target="_blank" rel="noreferrer" className={`${ON_DARK_BUTTON} shrink-0`}>
                  <ExternalLink size={14} /> Open in a new tab
                </a>
                {useExplorer && (
                  <button type="button" onClick={() => openInExplorer(preview.file.name)} className={`${ON_DARK_BUTTON} shrink-0`} data-show-in-explorer>
                    <FolderOpen size={14} /> Show in File Explorer
                  </button>
                )}
                <button type="button" onClick={closePreview} aria-label="Back to the folder" title="Back to the folder" className="ns-icon-btn ns-icon-btn-on-dark"><X /></button>
              </div>
              <div className="flex-1 min-h-0 grid place-items-center p-4">
                {preview.kind === 'image' && <img src={preview.url} alt={preview.file.name} className="max-w-full max-h-full object-contain" />}
                {preview.kind === 'video' && <VideoPlayer src={preview.url} autoPlay className="w-full h-full rounded-xl" label={preview.file.name} />}
                {preview.kind === 'audio' && <audio src={preview.url} controls autoPlay className="w-full max-w-md" />}
                {preview.kind === 'other' && <p className="text-sm text-cream/80">No preview for this kind of file. Open it in a new tab instead.</p>}
              </div>
            </div>
          )}
          {helperSetup && (
            <div className="absolute inset-0 overflow-y-auto bg-white" data-folder-helper-setup>
              <div className="max-w-xl mx-auto px-6 py-8">
                <div className="flex items-center gap-3">
                  <span className="ns-mark ns-mark-lavender"><FolderOpen /></span>
                  <h3 className="ns-t-heading">Open folders in File Explorer</h3>
                </div>
                <p className="mt-3 text-sm text-ink-soft">
                  Browsers are not allowed to start programs, so File Explorer needs a small helper on this computer. You set it up once.
                </p>
                <ol className="mt-5 space-y-2.5">
                  <SetupStep n={1} title="Download the setup file">
                    <button
                      type="button"
                      onClick={() => downloadFolderHelperSetup(rootName)}
                      className={`${OUTLINED_BUTTON} mt-1`}
                      data-download-helper
                    >
                      <Download size={14} /> Download setup
                    </button>
                  </SetupStep>
                  <SetupStep n={2} title={`Open ${FOLDER_HELPER_SETUP_NAME} from your downloads`}>
                    If Windows warns about it, choose More info, then Run anyway.
                  </SetupStep>
                  <SetupStep n={3} title="Press Enter, then choose your folder">
                    {rootName
                      ? <>Pick the folder called <span className="font-semibold text-ink">{rootName}</span>, the one this app saves into.</>
                      : 'Pick the folder this app saves into.'}
                  </SetupStep>
                  <SetupStep n={4} title="Come back and open the folder">
                    The browser asks once whether to open the helper. Tick Always allow, then choose Open.
                  </SetupStep>
                </ol>
                <p className="mt-4 text-xs text-ink-soft">
                  The helper only opens folders inside the one you choose and never runs files. To remove it, run the setup file again and type R.
                </p>
                <div className="mt-5 flex items-center gap-2">
                  <PrimaryButton onClick={finishHelperSetup}><FolderOpen size={14} /> Open in File Explorer</PrimaryButton>
                  <button type="button" onClick={() => setHelperSetup(null)} className="ns-btn ns-btn-quiet ns-btn-sm">
                    Not now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {explorerNote > 0 && (
          <div className="shrink-0 flex items-center gap-2 px-6 h-10 border-t border-cream-300 text-xs text-ink-soft" role="status" data-explorer-note>
            <FolderOpen size={14} className="text-lavender-deep shrink-0" aria-hidden="true" />
            Opening File Explorer. Nothing opened?
            <button type="button" onClick={() => { setExplorerNote(0); setHelperSetup({}); }} className="ns-link">
              Set up the helper
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SetupStep: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <li className="ns-well flex gap-3 p-3.5">
    <span className="w-6 h-6 grid place-items-center rounded-full bg-ink text-cream text-xs font-semibold shrink-0" aria-hidden="true">{n}</span>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-ink">{title}</p>
      <div className="mt-0.5 text-sm text-ink-soft">{children}</div>
    </div>
  </li>
);

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="h-full grid place-items-center">{children}</div>
);

/** Nothing to list: the one pattern for it (a round mark, a title, a line, at most one button). The mark is lavender, or the tone of what went wrong. */
const Message: React.FC<{ icon: React.ReactNode; title: string; text: string; tone?: 'warn' | 'danger'; children?: React.ReactNode }> = ({ icon, title, text, tone, children }) => (
  <div className="ns-empty h-full">
    <span className={`ns-mark ns-mark-lg ${tone ? '' : 'ns-mark-lavender'}`} data-tone={tone}>{icon}</span>
    <p className="ns-empty-title">{title}</p>
    <p className="ns-empty-text">{text}</p>
    {children && <div className="mt-2">{children}</div>}
  </div>
);

const PrimaryButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat">
    {children}
  </button>
);

const IconButton: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} className="ns-icon-btn">
    {children}
  </button>
);

export default LocalFolderWindow;
